<script setup lang="ts">
    import { computed, onMounted, ref, reactive, onUnmounted } from 'vue';
    import { useRouter } from 'vue-router';
    import chatService, { type ChatConversation } from '@/services/chatService';
    import { formatDate } from '../../utils';
    import { useNotificationStore } from '@/stores/notificationStore';
    import { onEvent, offEvent } from '@/config/socket';
    
    const router = useRouter();
    const notificationStore = useNotificationStore();
    const errorMessage = ref('');
    const loading = ref(false);
    const conversationsList = ref<ChatConversation[]>([]);
    const selectedItem = ref<ChatConversation | null>(null);
    const showFilters = ref(false);
    
    const filters = reactive({
        statut: '',
        type: '',
        search: '',
    });

    // Callback pour les nouveaux messages (rafraîchir la liste)
    const handleNewMessage = (data: any) => {
        console.log('[ServiceClientList] Nouveau message reçu:', data);
        // Rafraîchir la liste
        doListConversations();
    };

    // Obtenir le nombre de messages non lus pour une conversation
    function getUnreadCount(conversationId: number): number {
        return notificationStore.getUnreadForConversation(conversationId);
    }

    onMounted(() => {
        doListConversations();
        // Rafraîchir les compteurs
        notificationStore.fetchUnreadCount();
        // Écouter les nouveaux messages
        onEvent('new.message.admin', handleNewMessage);
    });

    onUnmounted(() => {
        offEvent('new.message.admin', handleNewMessage);
    });

    async function doListConversations() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const params: any = {};
            if (filters.statut) params.statut = filters.statut;
            if (filters.type) params.type = filters.type;
            if (filters.search) params.search = filters.search;
            params.sortColumn = 'updated_at';
            params.sortDir = 'desc';
            
            conversationsList.value = await chatService.getConversations(params);
        } catch(error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    function doSelectItem(item: ChatConversation) {
        selectedItem.value = item;
    }

    function doUnselectItem() {
        selectedItem.value = null;
    }

    function goToConversation(conversationId: number) {
        router.push(`/app/service-client/${conversationId}`);
    }

    function getStatutBadgeClass(statut: string) {
        switch(statut) {
            case 'open':
                return 'badge-success';
            case 'closed':
                return 'badge-grey';
            case 'pending':
                return 'badge-warning';
            default:
                return 'badge-grey';
        }
    }

    function getStatutLabel(statut: string) {
        switch(statut) {
            case 'open':
                return 'Ouvert';
            case 'closed':
                return 'Fermé';
            case 'pending':
                return 'En attente';
            default:
                return statut;
        }
    }

    function getTypeLabel(type: string) {
        const types: Record<string, string> = {
            'general': 'Général',
            'reclamation': 'Réclamation',
            'remboursement': 'Remboursement',
            'compte': 'Problème compte',
            'carte': 'Carte',
            'autre': 'Autre',
        };
        return types[type] || type;
    }
</script>

<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>

        <!--*** Widget *** -->
        <section class="s20">
            <div class="widget shadow-100 pb-2">
                <!--Card-->
                <div>
                    <div class="container-full-2 my-5">
                        <div class="s7">
                            <h4 class="font-semibold mt-4">Service Client <span class="text-sm text-grey-500">({{ conversationsList.length }})</span></h4>
                        </div>
                        <div class="s13 flex justify-content-right">
                            <button 
                                class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter"
                                :class="{ active: showFilters }"
                                @click="showFilters = !showFilters"
                            >
                                <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                                <span>Options</span>
                            </button>
                        </div>
                        <div class="clear-both"></div>
                        
                        <!--Options-->
                        <div class="filter-set-area" :class="{ active: showFilters }">
                            <div class="grid-filter6 mt-4 mb-0">
                                <div class="form-field-floating reactive sm">
                                    <input 
                                        type="text" 
                                        class="form-input" 
                                        name="search" 
                                        id="search" 
                                        v-model="filters.search"
                                        placeholder="Rechercher..."
                                        @keyup.enter="doListConversations"
                                    />
                                    <label for="search">Recherche (nom, email, téléphone)</label>
                                </div>
                                <div class="form-field-floating sm">
                                    <select class="form-input-select" name="statut" id="statut" v-model="filters.statut">
                                        <option value="">Tous les statuts</option>
                                        <option value="open">Ouvert</option>
                                        <option value="closed">Fermé</option>
                                        <option value="pending">En attente</option>
                                    </select>
                                    <label for="statut">Statut</label>
                                </div>
                                <div class="form-field-floating sm">
                                    <select class="form-input-select" name="type" id="type" v-model="filters.type">
                                        <option value="">Tous les types</option>
                                        <option value="general">Général</option>
                                        <option value="reclamation">Réclamation</option>
                                        <option value="remboursement">Remboursement</option>
                                        <option value="compte">Problème compte</option>
                                        <option value="carte">Carte</option>
                                        <option value="autre">Autre</option>
                                    </select>
                                    <label for="type">Type</label>
                                </div>
                                <button class="btn black normal justify-content-center mx-0 btn-filtre" @click="doListConversations">
                                    <img src="/icons/icon-search.svg" class="btn-icon" alt="" />
                                    <span>Rechercher</span>
                                </button>
                                <button class="btn white2 normal justify-content-center mx-0 btn-filtre" @click="filters.search = ''; filters.statut = ''; filters.type = ''; doListConversations()">
                                    <span>Réinitialiser</span>
                                </button>
                            </div>
                        </div>
                        <!---->
                    </div>
                    <table class="table separator skin1">
                        <thead>
                            <tr>
                                <th>Utilisateur</th>
                                <th>Type</th>
                                <th>Statut</th>
                                <th>Dernier message</th>
                                <th>Messages</th>
                                <th>Date</th>
                                <th class="flex-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="loading">
                                <tr>
                                    <td colspan="7">
                                        <div class="text-center text-grey-700">Chargement...</div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else-if="conversationsList.length == 0">
                                <tr>
                                    <td colspan="7">
                                        <div class="text-center text-grey-700">Aucune conversation trouvée</div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else v-for="(conversation, index) in conversationsList" :key="'conv_' + index">
                                <tr 
                                    class="text-grey-700 over-effect over-effect-scale" 
                                    :class="{ 'bg-primary-50': (conversation.unreadCount || 0) > 0 || getUnreadCount(conversation.id) > 0 }"
                                    @click="goToConversation(conversation.id)"
                                >
                                    <td>
                                        <div class="flex align-items-center">
                                            <div class="position-relative">
                                                <img 
                                                    v-if="conversation.user?.avatar" 
                                                    :src="conversation.user.avatar" 
                                                    class="size-md rounded-100 mr-3" 
                                                    alt=""
                                                />
                                                <div v-else class="size-md rounded-100 mr-3 bg-grey-200 flex align-items-center justify-content-center">
                                                    <span class="text-xs">{{ conversation.user?.prenom?.[0] || 'U' }}</span>
                                                </div>
                                                <!-- Badge messages non lus sur l'avatar -->
                                                <span 
                                                    v-if="(conversation.unreadCount || 0) > 0 || getUnreadCount(conversation.id) > 0" 
                                                    class="badge-notification"
                                                >
                                                    {{ (conversation.unreadCount || getUnreadCount(conversation.id)) > 9 ? '9+' : (conversation.unreadCount || getUnreadCount(conversation.id)) }}
                                                </span>
                                            </div>
                                            <div>
                                                <h6 class="mb-0" :class="{ 'font-bold': (conversation.unreadCount || 0) > 0 }">
                                                    {{ conversation.user?.prenom }} {{ conversation.user?.nom }}
                                                </h6>
                                                <span class="text-xs text-grey-500">{{ conversation.user?.email }}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td><h6>{{ getTypeLabel(conversation.type) }}</h6></td>
                                    <td>
                                        <span :class="['badge', getStatutBadgeClass(conversation.statut)]">
                                            {{ getStatutLabel(conversation.statut) }}
                                        </span>
                                    </td>
                                    <td>
                                        <div v-if="conversation.dernierMessage">
                                            <h6 class="mb-0 text-sm" :class="{ 'font-bold': (conversation.unreadCount || 0) > 0 }">
                                                {{ conversation.dernierMessage.texte.substring(0, 50) }}{{ conversation.dernierMessage.texte.length > 50 ? '...' : '' }}
                                            </h6>
                                            <span class="text-xs text-grey-500">{{ formatDate(conversation.dernierMessage.date) }}</span>
                                        </div>
                                        <span v-else class="text-grey-500">Aucun message</span>
                                    </td>
                                    <td>
                                        <h6>
                                            {{ conversation.messagesCount }}
                                            <span v-if="(conversation.unreadCount || 0) > 0" class="text-danger text-xs">
                                                ({{ conversation.unreadCount }} non lu{{ (conversation.unreadCount || 0) > 1 ? 's' : '' }})
                                            </span>
                                        </h6>
                                    </td>
                                    <td><h6>{{ formatDate(conversation.updated_at) }}</h6></td>
                                    <td class="flex-right mt-1" @click.stop>
                                        <button 
                                            class="btn" 
                                            @click="goToConversation(conversation.id)"
                                        >
                                            <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                            <span>Ouvrir</span>
                                        </button>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
                <div class="clear-both h-30"></div>
            </div>
        </section>
        
        <div class="clear-both h-30"></div>
    </section>
</template>
